import { Box, Button, CircularProgress, Typography } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import UPSnackBar from 'components/app-container/snackbar';
import DeleteModal from 'components/delete-modal';
import { UPDropdown, UPDropdownItem } from 'components/dropdown';
import UPDropdownWithSearch from 'components/dropdown-with-search';
import FiltersMenu from 'components/filters-menu';
import { StyledFilterIcon } from 'components/filters-menu/styles';
import UPSearchInput from 'components/input-with-search';
import { trackFiltersChanged } from 'services/analytics/filtersTracking';
import { CoefficientsPerWorkerClassOptions } from 'constants/CoefficientsPerWorkerclass';
import { CompanyRole } from 'constants/CompanyRole';
import { EmployeeStatus } from 'constants/EmployeeStatus';
import { toNumber } from 'lodash';
import useInfiniteScroll from 'hooks/useInfiniteScroll';
import { generateAgeFilterOptions } from 'modules/contracts/helpers';
import { PageFilter } from 'modules/contracts/types';
import { ApplicantStatus } from 'types/employee/IJobApplicant';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useJobApplicantsEnabled from 'hooks/useJobApplicantsEnabled';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store';
import { getCompaniesAsDropdownItemsAction } from 'store/actions/company-actions';
import { getLocationsDropdownItemsAction } from 'store/actions/company-locations-actions';
import {
  clearEmployeeAction,
  clearExistingEmployeeInfoAtOnboarding,
  downloadEmployeesAction,
  getAllEmployeesAction,
  onboardingEmployeeDetailsAction,
  onboardingEmployeeWithGeneralLinkAction,
  saveEmployeeSuccessAction,
  setEmployeeError,
  setOnboarding,
  setOnboardingWithGeneralLink,
} from 'store/actions/employee-actions';
import { setMenuStatusAction } from 'store/actions/menu-actions';
import { locationsDropdownItemsSelector } from 'store/selectors/company-locations-selectors';
import { companiesDropdownItemsSelector } from 'store/selectors/company-selectors';
import {
  canLoadMoreEmployeesSelector,
  currentPageSelector,
  employeeLoadingSelector,
  employeesErrorSelector,
  employeesSelector,
  numberOfEmployeesSelector,
  onboardingDetailsSelector,
  onboardingSelector,
  saveEmployeeSuccessSelector,
  updateEmployeeSuccessSelector,
} from 'store/selectors/employee-selector';
import {
  applicantsCurrentPageSelector,
  applicantsErrorSelector,
  applicantsLoadingSelector,
  applicantsSelector,
  canLoadMoreApplicantsSelector,
  promoteSuccessMessageSelector,
} from 'store/selectors/job-applicant-selectors';
import {
  clearApplicantsAction,
  getApplicantsAction,
  setPromoteSuccessMessageAction,
} from 'store/actions/job-applicant-actions';
import { activeMenuItemSelector } from 'store/selectors/menu-selectors';
import { profileSelector } from 'store/selectors/profile-selectors';
import {
  COMPANY__COMPANY,
  CONTRACTS__ARE_YOU_SURE,
  CONTRACTS__DEFAULT_ERROR,
  EMPLOYEES__INVITED,
  EMPLOYEES__ADD_NEW_EMPLOYEE,
  EMPLOYEES__ALREADY_EXISTS_ERROR,
  EMPLOYEES__COMPANY_NO_EMPLOYEES,
  EMPLOYEES__IBAN_ERROR,
  EMPLOYEES__INSS_ERROR,
  EMPLOYEES__INVITATION_SENT_SUCCESSFULLY,
  EMPLOYEES__INVITE,
  EMPLOYEES__PERMANENT_CHANGE_VALIDATION,
  EMPLOYEES__SEARCH_EMPLOYEE_NAME,
  EMPLOYEES__SELECT_A_COMPANY,
  EMPLOYEES__SORT_ALPHABETICAL,
  EMPLOYEES__SORT_CRITERIA,
  EMPLOYEES__SORT_NEWEST,
  EMPLOYEE_STATUS__REQUIRES_ACTION,
  EMPLOYEE__COMPANY_LOCATION,
  GENERAL__AGE,
  GENERAL__ALL,
  GENERAL__LINK_COPIED,
  GENERAL__NAME,
  GENERAL__NO,
  GENERAL__SELECT,
  GENERAL__WORKERCLASS,
  GENERAL__YES,
  MENU__EMPLOYEES,
  EMPLOYEES__ELIGIBLE,
  EMPLOYEES__TERMINATED,
  EMPLOYEES__TERMINATED_FILTER,
  EMPLOYEES__ELIGIBLE_FILTER,
  APPLICANTS__LOAD_FAILED,
  APPLICANTS__NO_APPLICANTS,
  APPLICANTS__SEARCH_APPLICANT_NAME,
  APPLICANTS__STATUS_DISCARDED,
  APPLICANTS__STATUS_SUBMITTED,
  EMPLOYEES__APPLICANTS,
  EMPLOYEES__STATUS,
} from 'translations/constants';
import { ReactComponent as Chevron } from '../../assets/icons/Chevron.svg';
import { ReactComponent as SortIcon } from '../../assets/icons/SortIcon.svg';
import { AddEditEmployeeModal } from './add-edit-employee-modal';
import ApplicantCard from './ApplicantCard';
import ApplicantDetailsModal from './applicant-details-modal/ApplicantDetailsModal';
import EmployeeCard from './EmployeeCard';
import InviteEmployeeModal from './invite-employee-modal';
import {
  AddEmployeeButton,
  AddInviteButtonsContainer,
  AlertStyled,
  CardsGrid,
  CollapseSearchFiltersContainer,
  EmployeesSwitchContainer,
  EmployeesSwitchSegment,
  EmployeesSwitchTrack,
  FiltersContainer,
  FiltersEmployeeBox,
  FiltersLeftContainer,
  FiltersRightContainer,
  NameEmployeeBox,
  SortIconBox,
  SortIconMobileBox,
  SquareFilterButton,
  StyledDownloadIcon,
  StyledEmployeeIcon,
  StyledInviteIcon,
  UPDropdownStyled,
} from './styles';

const EMPLOYEES_PAGE_SIZE = 36;

/**
 * Sentinel for the applicant Status dropdown's "All" option. Not an ApplicantStatus value — it
 * means "send every status the API accepts", resolved in buildApplicantParams.
 */
const APPLICANT_STATUS_ALL = 'All';

const Employees = () => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { companyToken, inviteToken, pCompanyId, pEmployeeName, pEmployeeId } = useParams();

  const { generalToken } = useParams();
  const activeItem = useAppSelector(activeMenuItemSelector);
  const saveNewEmployeeIsSuccessful = useAppSelector(saveEmployeeSuccessSelector);
  const employees = useAppSelector(employeesSelector);
  const updateEmployeeSuccessful = useAppSelector(updateEmployeeSuccessSelector);
  const employeeError = useAppSelector(employeesErrorSelector);
  const onboardingDetails = useAppSelector(onboardingDetailsSelector);
  const userCompanyId = useAppSelector(profileSelector)?.companyId || null;
  const userRole = useAppSelector(profileSelector)?.role;
  const isAccountManager = userRole === CompanyRole.ACCOUNT_MANAGER;
  const isAdmin = userRole === CompanyRole.ADMIN;
  const isAdminOrAccountManager = isAdmin || isAccountManager;
  const employeeOnboarding = useAppSelector(onboardingSelector);
  const isEmployee = useAppSelector(profileSelector)?.role === CompanyRole.EMPLOYEE;
  const companiesDropdownItems = useAppSelector(companiesDropdownItemsSelector);
  const employeesNumber = useAppSelector(numberOfEmployeesSelector);
  const locationsList = useAppSelector(locationsDropdownItemsSelector);
  const loadedPage = useAppSelector(currentPageSelector);
  const canLoadMore = useAppSelector(canLoadMoreEmployeesSelector);
  const isLoadingEmployees = useAppSelector(employeeLoadingSelector);
  const applicants = useAppSelector(applicantsSelector);
  const applicantsError = useAppSelector(applicantsErrorSelector);
  const isLoadingApplicants = useAppSelector(applicantsLoadingSelector);
  const applicantsLoadedPage = useAppSelector(applicantsCurrentPageSelector);
  const canLoadMoreApplicants = useAppSelector(canLoadMoreApplicantsSelector);
  const promoteSuccessMessage = useAppSelector(promoteSuccessMessageSelector);

  const [companyId, setCompanyId] = useState<string>(userCompanyId?.toString() ?? pCompanyId);
  const applicantsEnabled = useJobApplicantsEnabled(companyId);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [openEmployeeError, setOpenEmployeeError] = useState<boolean>(false);
  const [openAddEditModal, setOpenAddEditModal] = useState<boolean>(false);
  const [openApplicantId, setOpenApplicantId] = useState<number | null>(null);

  const [displayPendingEmployees, setDisplayPendingEmployees] = useState<string>('');
  const [openInviteModal, setOpenInviteModal] = useState<boolean>(false);
  const [openConfirmCloseModal, setOpenConfirmCloseModal] = useState<boolean>(false);
  const [isEmployeeModalOpen, setIsEmployeeModalOpen] = useState<boolean>(isEmployee);
  const itemsPerPage = EMPLOYEES_PAGE_SIZE;
  const [employeeCompanyId, setEmployeeCompanyId] = useState<number>(null);
  const [age, setAge] = useState<string>('');
  const [workType, setWorkType] = useState<string>('');
  const [location, setLocation] = useState<string>('');
  const [invited, setInvited] = useState<string>('');
  const [terminated, setTerminated] = useState<string>('2');
  const [eligible, setEligible] = useState<string>('');
  const [name, setName] = useState<string>(pEmployeeName ?? '');
  const [nameToSend, setNameToSend] = useState<string>(pEmployeeName ?? '');
  const [sortCriteria, setSortCriteria] = useState<string>('1');
  const [sortOrder, setSortOrder] = useState<number>(0);
  const [isContainerVisible, setIsContainerVisible] = useState(true);
  const [copiedToClipboard, setCopiedToClipboard] = useState<boolean>(false);
  const [inviteSent, setInviteSent] = useState<boolean>(false);
  const [filtersPopoverAnchorEl, setFiltersPopoverAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTab, setSelectedTab] = useState<'employees' | 'applicants'>('employees');
  const [applicantStatus, setApplicantStatus] = useState<string>(String(ApplicantStatus.Submitted));
  const [applicantName, setApplicantName] = useState<string>('');
  const [applicantNameToSend, setApplicantNameToSend] = useState<string>('');
  const [applicantSortCriteria, setApplicantSortCriteria] = useState<string>('1');
  const [applicantSortOrder, setApplicantSortOrder] = useState<number>(0);

  const workerclassList = useMemo(() => CoefficientsPerWorkerClassOptions(), []);
  const ageOptions = generateAgeFilterOptions();

  const employeeFilters = useMemo((): PageFilter[] => {
    const activeFilterOptions: UPDropdownItem[] = [
      {
        value: '1',
        label: t(GENERAL__YES),
      },
      {
        value: '2',
        label: t(GENERAL__NO),
      },
    ];

    const filters: PageFilter[] = [];

    const companyBasedFilters: PageFilter[] = [
      {
        label: t(EMPLOYEE__COMPANY_LOCATION),
        value: location,
        setValue: setLocation,
        items: locationsList,
        component: UPDropdownWithSearch,
        gtmEvent: 'employees-company-branch-filter',
      },
      {
        label: t(GENERAL__WORKERCLASS),
        value: workType,
        setValue: setWorkType,
        items: workerclassList,
        component: UPDropdown,
        canBeEmpty: true,
        gtmEvent: 'employees-workerclass-filter',
      },
      {
        label: t(GENERAL__AGE),
        value: age,
        setValue: setAge,
        items: ageOptions,
        component: UPDropdown,
        canBeEmpty: true,
        gtmEvent: 'employees-age-group-filter',
      },
      {
        label: t(EMPLOYEES__INVITED),
        value: invited,
        setValue: setInvited,
        items: activeFilterOptions,
        component: UPDropdown,
        gtmEvent: 'employees-invited-filter',
      },
      {
        label: t(EMPLOYEES__TERMINATED_FILTER),
        value: terminated,
        setValue: setTerminated,
        items: [
          {
            value: '1',
            label: t(GENERAL__YES),
          },
          {
            value: '2',
            label: t(GENERAL__NO),
          },
        ],
        component: UPDropdown,
        gtmEvent: 'employees-terminated-filter',
      },
      {
        label: t(EMPLOYEES__ELIGIBLE_FILTER),
        value: eligible,
        setValue: setEligible,
        items: [
          {
            value: '1',
            label: t(GENERAL__YES),
          },
          {
            value: '2',
            label: t(GENERAL__NO),
          },
        ],
        component: UPDropdown,
        gtmEvent: 'employees-eligible-filter',
      },
    ];

    if (companyId) {
      filters.push(...companyBasedFilters);

      if (isAdminOrAccountManager) {
        filters.push({
          label: t(EMPLOYEE_STATUS__REQUIRES_ACTION),
          value: displayPendingEmployees,
          setValue: setDisplayPendingEmployees,
          items: [
            {
              value: '1',
              label: t(GENERAL__YES),
            },
            {
              value: '2',
              label: t(GENERAL__NO),
            },
          ],
          component: UPDropdown,
          gtmEvent: 'employees-requires-action-filter',
        });
      }
    }

    return filters;
  }, [
    t,
    location,
    locationsList,
    workType,
    workerclassList,
    age,
    ageOptions,
    invited,
    terminated,
    eligible,
    isAdminOrAccountManager,
    displayPendingEmployees,
    companyId,
  ]);

  const employeeErrorMessages = useMemo(() => {
    return {
      default: t(CONTRACTS__DEFAULT_ERROR),
      inss: t(EMPLOYEES__INSS_ERROR),
      iban: t(EMPLOYEES__IBAN_ERROR),
      permanent: t(EMPLOYEES__PERMANENT_CHANGE_VALIDATION),
      inss_exists: t(EMPLOYEES__ALREADY_EXISTS_ERROR),
    };
  }, [t]);

  const handleCollapseFiltersClick = () => {
    setIsContainerVisible(prevState => !prevState);
  };

  const handleSortIconClick = () => {
    if (selectedTab === 'applicants') {
      setApplicantSortOrder(applicantSortOrder === 0 ? 1 : 0);
      return;
    }
    setSortOrder(sortOrder === 0 ? 1 : 0);
  };

  const resetFilters = () => {
    setName('');
    setNameToSend('');
    setAge('');
    setLocation('');
    setInvited('');
    setTerminated('2');
    setEligible('');
    setSortOrder(0);
    setSortCriteria('1');
    setWorkType('');
    setDisplayPendingEmployees('');
  };

  const displayEmployees = useCallback(() => {
    if (isAdminOrAccountManager && !companyId) {
      return (
        <AlertStyled severity="warning" icon={false} variant="filled">
          {t(EMPLOYEES__SELECT_A_COMPANY)}
        </AlertStyled>
      );
    }
    if (employees.length > 0) {
      return employees.map(employee => (
        <EmployeeCard
          employee={employee}
          key={employee.id}
          companyId={companyId}
          setCopied={setCopiedToClipboard}
          setInviteSent={setInviteSent}
          onClickEdit={employeeId => {
            setOpenAddEditModal(true);
            setOpenEmployeeError(false);
            dispatch(setEmployeeError(null));
            setIsEdit(true);
            navigate(`/employees/edit/${companyId}/${employeeId}`);
            if (isAdminOrAccountManager && displayPendingEmployees) {
              setCompanyId(employee.companyId.toString());
              setEmployeeCompanyId(employee.companyId);
            }
          }}
        />
      ));
    }
    return t(EMPLOYEES__COMPANY_NO_EMPLOYEES);
  }, [isAdminOrAccountManager, companyId, displayPendingEmployees, employees, t, dispatch, navigate]);

  const onClickFilterButton = event => {
    setFiltersPopoverAnchorEl(event.currentTarget);
  };

  const onCloseFilterPopover = () => {
    setFiltersPopoverAnchorEl(null);
  };

  const onClearApplicantsFilters = () => {
    setApplicantStatus(String(ApplicantStatus.Submitted));
  };

  const onClearEmployeesFilters = () => {
    setLocation('');
    setWorkType('');
    setAge('');
    setInvited('');
    setTerminated('2');
    setEligible('');
    setDisplayPendingEmployees('');
  };

  const sortCriteriaDropdownOptions: UPDropdownItem[] = [
    { value: '1', label: t(EMPLOYEES__SORT_NEWEST) },
    { value: '2', label: t(EMPLOYEES__SORT_ALPHABETICAL) },
  ];

  const applicantStatusDropdownOptions: UPDropdownItem[] = useMemo(
    () => [
      { value: APPLICANT_STATUS_ALL, label: t(GENERAL__ALL) },
      { value: String(ApplicantStatus.Submitted), label: t(APPLICANTS__STATUS_SUBMITTED) },
      { value: String(ApplicantStatus.Discarded), label: t(APPLICANTS__STATUS_DISCARDED) },
    ],
    [t],
  );

  // Applicants have a single filter (Status), but it goes through the same FiltersMenu popover the
  // employees tab uses so both tabs keep one filtering entry point. Status has no empty option —
  // "All" is what clearing falls back to conceptually, but the default is Submitted, so Clear all
  // restores that rather than emptying the dropdown.
  const applicantFilters = useMemo(
    (): PageFilter[] => [
      {
        label: t(EMPLOYEES__STATUS),
        value: applicantStatus,
        setValue: setApplicantStatus,
        items: applicantStatusDropdownOptions,
        component: UPDropdown,
        gtmEvent: 'applicants-status-filter',
      },
    ],
    [t, applicantStatus, applicantStatusDropdownOptions],
  );

  // The applicant endpoint's own sortCriteria convention: 1 = submission date, 0 = last name then
  // first name — not the same numbering the employee endpoint uses for these same two labels.
  const applicantSortCriteriaDropdownOptions: UPDropdownItem[] = [
    { value: '1', label: t(EMPLOYEES__SORT_NEWEST) },
    { value: '0', label: t(EMPLOYEES__SORT_ALPHABETICAL) },
  ];

  const getStatus = useCallback((): number[] => {
    if (displayPendingEmployees === '1') {
      return [EmployeeStatus.RequiresAction];
    }

    if (displayPendingEmployees === '2') {
      return [EmployeeStatus.Ready, EmployeeStatus.Deployed, EmployeeStatus.Invited];
    }

    return [EmployeeStatus.Ready, EmployeeStatus.Deployed, EmployeeStatus.Invited, EmployeeStatus.RequiresAction];
  }, [displayPendingEmployees]);

  const buildEmployeeFilters = useCallback(
    (pageIndex: number) => ({
      companyId,
      pageSize: itemsPerPage,
      pageIndex,
      workType: workType === '' ? undefined : workType,
      age,
      locationId: location === '' ? undefined : toNumber(location),
      invited,
      terminated,
      eligible,
      sortOrder,
      sortCriteria: toNumber(sortCriteria),
      name: nameToSend === '' ? undefined : nameToSend,
      status: getStatus(),
    }),
    [
      companyId,
      itemsPerPage,
      workType,
      age,
      location,
      invited,
      terminated,
      eligible,
      sortOrder,
      sortCriteria,
      nameToSend,
      getStatus,
    ],
  );

  const loadMoreEmployees = useCallback(() => {
    if (!companyId || isLoadingEmployees || !canLoadMore) return;
    dispatch(getAllEmployeesAction(buildEmployeeFilters(loadedPage + 1)));
  }, [companyId, isLoadingEmployees, canLoadMore, dispatch, buildEmployeeFilters, loadedPage]);

  const sentinelRef = useInfiniteScroll<HTMLDivElement>({
    enabled: Boolean(companyId) && canLoadMore && !isLoadingEmployees,
    onLoadMore: loadMoreEmployees,
  });

  // Promoted (3) is never offered as an option and is stripped server-side regardless.
  const activeApplicantStatuses = useMemo(
    () =>
      applicantStatus === APPLICANT_STATUS_ALL
        ? [ApplicantStatus.Submitted, ApplicantStatus.Discarded]
        : [Number(applicantStatus) as ApplicantStatus],
    [applicantStatus],
  );

  const visibleApplicants = useMemo(
    () => applicants.filter(applicant => activeApplicantStatuses.includes(applicant.status)),
    [applicants, activeApplicantStatuses],
  );

  const buildApplicantParams = useCallback(
    (pageIndex: number) => ({
      companyId: toNumber(companyId),
      statuses: activeApplicantStatuses,
      name: applicantNameToSend === '' ? undefined : applicantNameToSend,
      pageIndex,
      pageSize: itemsPerPage,
      sortCriteria: toNumber(applicantSortCriteria),
      sortOrder: applicantSortOrder,
    }),
    [companyId, activeApplicantStatuses, applicantNameToSend, itemsPerPage, applicantSortCriteria, applicantSortOrder],
  );

  useEffect(() => {
    if (selectedTab === 'applicants' && applicantsEnabled && companyId) {
      dispatch(getApplicantsAction(buildApplicantParams(1)));
    }
  }, [selectedTab, applicantsEnabled, companyId, dispatch, buildApplicantParams]);

  const loadMoreApplicants = useCallback(() => {
    if (!companyId || isLoadingApplicants || !canLoadMoreApplicants) return;
    dispatch(getApplicantsAction(buildApplicantParams(applicantsLoadedPage + 1)));
  }, [companyId, isLoadingApplicants, canLoadMoreApplicants, dispatch, buildApplicantParams, applicantsLoadedPage]);

  // Its own sentinel: this endpoint pages for every role, unlike the employee one.
  const applicantSentinelRef = useInfiniteScroll<HTMLDivElement>({
    enabled: selectedTab === 'applicants' && Boolean(companyId) && canLoadMoreApplicants && !isLoadingApplicants,
    onLoadMore: loadMoreApplicants,
  });

  const displayApplicants = useCallback(() => {
    if (isAdminOrAccountManager && !companyId) {
      return (
        <AlertStyled severity="warning" icon={false} variant="filled">
          {t(EMPLOYEES__SELECT_A_COMPANY)}
        </AlertStyled>
      );
    }
    // Replaces the applicant cards only — the employee list is a separate fetch and stays intact.
    if (applicantsError) {
      return (
        <AlertStyled severity="error" icon={false} variant="filled">
          {t(APPLICANTS__LOAD_FAILED)}
        </AlertStyled>
      );
    }
    if (visibleApplicants.length === 0) {
      if (isLoadingApplicants) return null;
      // Also the "manager manages no branches" case: the API answers 200 with an empty page.
      // Plain text, matching how the employee list itself renders its own empty state.
      return t(APPLICANTS__NO_APPLICANTS);
    }
    // Keyed by id, never email: a reapplying applicant legitimately repeats name and email.
    return visibleApplicants.map(applicant => (
      <ApplicantCard applicant={applicant} key={applicant.id} onClick={setOpenApplicantId} />
    ));
  }, [isAdminOrAccountManager, companyId, applicantsError, visibleApplicants, isLoadingApplicants, t]);

  // ─── Analytics: which overview filters actually get used ─────────────────
  const filterInteractionIndexRef = useRef(0);
  const pendingFiltersRef = useRef<{ changedFilters: string[]; filterState: Record<string, unknown> } | null>(null);

  const recordFilterChange = useCallback((changedFilters: string[], filterState: Record<string, unknown>) => {
    pendingFiltersRef.current = { changedFilters, filterState };
  }, []);

  useEffect(() => {
    if (!pendingFiltersRef.current) return;
    const { changedFilters, filterState } = pendingFiltersRef.current;
    pendingFiltersRef.current = null;
    trackFiltersChanged({
      changedFilters,
      filterState: { ...filterState, mode: selectedTab },
      rowsVisibleCount: selectedTab === 'applicants' ? visibleApplicants.length : employees.length,
      interactionIndex: filterInteractionIndexRef.current,
    });
    filterInteractionIndexRef.current += 1;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees, visibleApplicants]);
  const employeeFilterState = useMemo<Record<string, unknown>>(
    () => ({ location, workType, age, invited, terminated, eligible, name: nameToSend, sortCriteria, sortOrder }),
    [location, workType, age, invited, terminated, eligible, nameToSend, sortCriteria, sortOrder],
  );

  const applicantFilterState = useMemo<Record<string, unknown>>(
    () => ({
      status: applicantStatus,
      name: applicantNameToSend,
      sortCriteria: applicantSortCriteria,
      sortOrder: applicantSortOrder,
    }),
    [applicantStatus, applicantNameToSend, applicantSortCriteria, applicantSortOrder],
  );

  const previousEmployeeFiltersRef = useRef(employeeFilterState);
  useEffect(() => {
    const previous = previousEmployeeFiltersRef.current;
    const changed = Object.keys(employeeFilterState).filter(key => employeeFilterState[key] !== previous[key]);
    previousEmployeeFiltersRef.current = employeeFilterState;
    if (changed.length > 0 && selectedTab === 'employees') {
      recordFilterChange(changed, employeeFilterState);
    }
  }, [employeeFilterState, recordFilterChange, selectedTab]);

  const previousApplicantFiltersRef = useRef(applicantFilterState);
  useEffect(() => {
    const previous = previousApplicantFiltersRef.current;
    const changed = Object.keys(applicantFilterState).filter(key => applicantFilterState[key] !== previous[key]);
    previousApplicantFiltersRef.current = applicantFilterState;
    if (changed.length > 0 && selectedTab === 'applicants') {
      recordFilterChange(changed, applicantFilterState);
    }
  }, [applicantFilterState, recordFilterChange, selectedTab]);
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (employeeError) setOpenEmployeeError(true);
  }, [employeeError]);

  useEffect(() => {
    if (!applicantsEnabled && selectedTab === 'applicants') {
      setSelectedTab('employees');
      dispatch(clearApplicantsAction());
    }
  }, [applicantsEnabled, selectedTab, dispatch]);

  useEffect(() => {
    if (companyId && !isEmployee) {
      dispatch(getLocationsDropdownItemsAction({ companyId: toNumber(companyId) }));
    }
  }, [companyId, isEmployee, dispatch]);

  useEffect(() => {
    if (activeItem !== t(MENU__EMPLOYEES) && !isEmployee) {
      dispatch(setMenuStatusAction(t(MENU__EMPLOYEES)));
    }
  }, [activeItem, dispatch, t, isEmployee]);

  useEffect(() => {
    if (companyToken && inviteToken && !employeeOnboarding) {
      dispatch(setOnboarding(true));
      dispatch(onboardingEmployeeDetailsAction({ companyToken, inviteToken }));
    }
  }, [companyToken, dispatch, employeeOnboarding, inviteToken, onboardingDetails]);

  useEffect(() => {
    if (generalToken && !employeeOnboarding) {
      dispatch(setOnboarding(true));
      dispatch(setOnboardingWithGeneralLink(true));
      dispatch(onboardingEmployeeWithGeneralLinkAction({ inviteToken: generalToken }));
    }
  }, [generalToken, dispatch, employeeOnboarding, onboardingDetails]);

  useEffect(() => {
    if (!isEmployee && !companyToken && !inviteToken && !generalToken && companyId) {
      dispatch(getAllEmployeesAction(buildEmployeeFilters(1)));
    }
  }, [dispatch, isEmployee, companyToken, inviteToken, generalToken, companyId, buildEmployeeFilters]);

  useEffect(() => {
    if (isAdminOrAccountManager) {
      dispatch(getCompaniesAsDropdownItemsAction());
    }
  }, [isAdminOrAccountManager, dispatch]);

  useEffect(() => {
    if (companyId && (saveNewEmployeeIsSuccessful || updateEmployeeSuccessful)) {
      dispatch(getAllEmployeesAction(buildEmployeeFilters(1)));
      dispatch(saveEmployeeSuccessAction(false));
    }
  }, [dispatch, saveNewEmployeeIsSuccessful, companyId, updateEmployeeSuccessful, buildEmployeeFilters]);

  useEffect(() => {
    if (!companyId) {
      resetFilters();
    }
  }, [companyId]);

  const onDownloadEmployees = () => {
    dispatch(
      downloadEmployeesAction({
        companyId,
        pageSize: employeesNumber || itemsPerPage,
        pageIndex: 1,
        workType: workType === '' ? undefined : workType,
        age: age === '' ? undefined : toNumber(age),
        locationId: location === '' ? undefined : toNumber(location),
        invited: toNumber(invited),
        terminated: toNumber(terminated),
        eligible: toNumber(eligible),
        sortOrder,
        sortCriteria: toNumber(sortCriteria),
        name: nameToSend === '' ? undefined : nameToSend,
      }),
    );
  };

  if (employeeOnboarding) {
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
        <AddEditEmployeeModal
          open={Boolean(companyToken && inviteToken) || Boolean(generalToken)}
          onClose={() => {
            setOpenConfirmCloseModal(true);
          }}
          employeeId={null}
          companyId={toNumber(onboardingDetails?.companyId)}
        />
        {openConfirmCloseModal && (
          <DeleteModal
            open={openConfirmCloseModal}
            content={t(CONTRACTS__ARE_YOU_SURE)}
            onClose={() => {
              setOpenConfirmCloseModal(false);
            }}
            onDelete={() => {
              navigate('/auth/login');
              setOpenConfirmCloseModal(false);
              dispatch(clearEmployeeAction());
              dispatch(clearExistingEmployeeInfoAtOnboarding());
              dispatch(setOnboarding(false));
            }}
          />
        )}
      </Box>
    );
  }

  if (isEmployee) {
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
        <AddEditEmployeeModal
          open={isEmployeeModalOpen}
          onClose={() => {
            setIsEmployeeModalOpen(false);
            dispatch(setMenuStatusAction('Dashboard'));
            navigate('/dashboard');
          }}
          employeeId={0}
          companyId={0}
          isEdit
        />
      </Box>
    );
  }

  return (
    <Box>
      <CollapseSearchFiltersContainer>
        <Button
          sx={{
            transform: `rotate(${isContainerVisible ? '0deg' : '180deg'})`,
            transition: 'transform 0.2s ease-in-out',
            cursor: 'pointer',
            alignSelf: 'center',
          }}
          onClick={handleCollapseFiltersClick}
        >
          <Chevron />
        </Button>
      </CollapseSearchFiltersContainer>
      {isContainerVisible && (
        <FiltersContainer>
          <FiltersLeftContainer>
            <AddInviteButtonsContainer>
              <AddEmployeeButton
                text={
                  <Tooltip title={t(EMPLOYEES__ADD_NEW_EMPLOYEE)} placement="top">
                    <Box display="flex" alignItems="center">
                      <Typography fontSize="xx-large"> + </Typography>
                      <StyledEmployeeIcon />
                    </Box>
                  </Tooltip>
                }
                onClick={() => {
                  setOpenAddEditModal(true);
                  setOpenEmployeeError(false);
                  dispatch(setEmployeeError(null));
                }}
                disabled={(isAdminOrAccountManager && !companyId) || selectedTab === 'applicants'}
              />
              <AddEmployeeButton
                text={
                  <Tooltip title={t(EMPLOYEES__INVITE)} placement="top">
                    <Box display="flex" alignItems="center">
                      <Typography fontSize="xx-large"> + </Typography>
                      <StyledInviteIcon />
                    </Box>
                  </Tooltip>
                }
                onClick={() => setOpenInviteModal(true)}
                disabled={isAdminOrAccountManager && !companyId}
              />
              {!isAccountManager && (
                <AddEmployeeButton
                  text={
                    <Tooltip title="Excel export" placement="top">
                      <StyledDownloadIcon />
                    </Tooltip>
                  }
                  onClick={onDownloadEmployees}
                  disabled={
                    employees.length === 0 || (isAdminOrAccountManager && !companyId) || selectedTab === 'applicants'
                  }
                />
              )}
            </AddInviteButtonsContainer>
            {isAdminOrAccountManager && (
              <Box sx={{ width: '280px', display: 'inline-block' }}>
                <UPDropdownWithSearch
                  value={companyId}
                  onChange={selectedItem => {
                    setCompanyId(selectedItem);
                    resetFilters();
                  }}
                  placeholder={t(GENERAL__SELECT)}
                  items={companiesDropdownItems}
                  label={t(COMPANY__COMPANY)}
                  disableClearable={false}
                />
              </Box>
            )}
            <NameEmployeeBox>
              <UPSearchInput
                label={t(GENERAL__NAME)}
                placeholder={
                  selectedTab === 'applicants'
                    ? t(APPLICANTS__SEARCH_APPLICANT_NAME)
                    : t(EMPLOYEES__SEARCH_EMPLOYEE_NAME)
                }
                value={selectedTab === 'applicants' ? applicantName : name}
                onChange={value => {
                  if (selectedTab === 'applicants') {
                    setApplicantName(value);
                    if (!value) setApplicantNameToSend('');
                    return;
                  }
                  setName(value);
                  if (!value) setNameToSend('');
                }}
                onSearch={() => {
                  if (selectedTab === 'applicants') {
                    setApplicantNameToSend(applicantName);
                    return;
                  }
                  setNameToSend(name);
                }}
                disabled={!companyId}
                gtmEvent="employees-name-search"
              />
            </NameEmployeeBox>
            {/* Employee-only: the applicant endpoint sorts by submission date and ignores these. */}
            {selectedTab === 'employees' && (
              <FiltersEmployeeBox>
                <UPDropdownStyled
                  value={sortCriteria}
                  onChange={event => {
                    setSortCriteria(event.target.value);
                    setSortOrder(0);
                  }}
                  items={sortCriteriaDropdownOptions}
                  label={t(EMPLOYEES__SORT_CRITERIA)}
                  disabled={!companyId}
                  gtmEvent="employees-sort-dropdown"
                />
              </FiltersEmployeeBox>
            )}
            {selectedTab === 'applicants' && (
              <FiltersEmployeeBox>
                <UPDropdownStyled
                  value={applicantSortCriteria}
                  onChange={event => {
                    setApplicantSortCriteria(event.target.value);
                    setApplicantSortOrder(0);
                  }}
                  items={applicantSortCriteriaDropdownOptions}
                  label={t(EMPLOYEES__SORT_CRITERIA)}
                  disabled={!companyId}
                  gtmEvent="applicants-sort-dropdown"
                />
              </FiltersEmployeeBox>
            )}
            <SortIconBox
              sortOrder={selectedTab === 'applicants' ? applicantSortOrder : sortOrder}
              onClick={handleSortIconClick}
              data-gtm-event="employees-sort-icon"
            >
              <SortIcon />
            </SortIconBox>
          </FiltersLeftContainer>
          <FiltersRightContainer>
            <SquareFilterButton
              text={
                <Box display="flex" alignItems="center">
                  <StyledFilterIcon />
                </Box>
              }
              onClick={onClickFilterButton}
              ref={filtersPopoverAnchorEl}
              disabled={!companyId}
            />
            <FiltersMenu
              open={Boolean(filtersPopoverAnchorEl)}
              anchorEl={filtersPopoverAnchorEl}
              onClose={onCloseFilterPopover}
              onApplyFilters={onCloseFilterPopover}
              onClearFilters={selectedTab === 'applicants' ? onClearApplicantsFilters : onClearEmployeesFilters}
              filters={selectedTab === 'applicants' ? applicantFilters : employeeFilters}
            />
            {applicantsEnabled && (
              <EmployeesSwitchContainer>
                <EmployeesSwitchTrack role="tablist">
                  <EmployeesSwitchSegment
                    type="button"
                    role="tab"
                    aria-selected={selectedTab === 'employees'}
                    selected={selectedTab === 'employees'}
                    onClick={() => setSelectedTab('employees')}
                  >
                    {t(MENU__EMPLOYEES)}
                  </EmployeesSwitchSegment>
                  <EmployeesSwitchSegment
                    type="button"
                    role="tab"
                    aria-selected={selectedTab === 'applicants'}
                    selected={selectedTab === 'applicants'}
                    onClick={() => setSelectedTab('applicants')}
                  >
                    {t(EMPLOYEES__APPLICANTS)}
                  </EmployeesSwitchSegment>
                </EmployeesSwitchTrack>
              </EmployeesSwitchContainer>
            )}
            <SortIconMobileBox
              sortOrder={selectedTab === 'applicants' ? applicantSortOrder : sortOrder}
              onClick={handleSortIconClick}
            >
              <SortIcon />
            </SortIconMobileBox>
          </FiltersRightContainer>
        </FiltersContainer>
      )}
      <CardsGrid>{selectedTab === 'applicants' ? displayApplicants() : displayEmployees()}</CardsGrid>
      {selectedTab === 'employees' && companyId && employees.length > 0 && (
        <>
          <Box ref={sentinelRef} sx={{ height: '1px', width: '100%' }} />
          {isLoadingEmployees && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', paddingTop: '16px' }}>
              <CircularProgress size={28} />
            </Box>
          )}
        </>
      )}
      {selectedTab === 'applicants' && companyId && applicants.length > 0 && (
        <>
          <Box ref={applicantSentinelRef} sx={{ height: '1px', width: '100%' }} />
          {isLoadingApplicants && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', paddingTop: '16px' }}>
              <CircularProgress size={28} />
            </Box>
          )}
        </>
      )}

      {openAddEditModal && (
        <AddEditEmployeeModal
          open={openAddEditModal}
          onClose={() => {
            setOpenAddEditModal(false);
            navigate('/employees');
            if (isEdit) {
              setIsEdit(false);
              dispatch(clearEmployeeAction());
            }
          }}
          isEdit={isEdit}
          employeeId={+pEmployeeId}
          companyId={toNumber(companyId)}
          pendingEmployeeCompanyId={displayPendingEmployees ? employeeCompanyId : null}
          onSavePendingEmployee={() => setEmployeeCompanyId(null)}
        />
      )}
      {openInviteModal && (
        <InviteEmployeeModal open={openInviteModal} onClose={() => setOpenInviteModal(false)} companyId={companyId} />
      )}

      {openApplicantId && (
        <ApplicantDetailsModal
          open
          onClose={() => setOpenApplicantId(null)}
          applicantId={openApplicantId}
          companyId={toNumber(companyId)}
        />
      )}

      {employeeError && (
        <UPSnackBar
          vertical="bottom"
          horizontal="right"
          open={openEmployeeError}
          content={
            employeeError === 'inss' ||
            employeeError === 'inss_exists' ||
            employeeError === 'email' ||
            employeeError === 'iban' ||
            employeeError === 'default' ||
            employeeError === 'permanent'
              ? employeeErrorMessages[employeeError]
              : t(employeeError)
          }
          type="error"
          onClose={() => {
            setOpenEmployeeError(false);
            dispatch(setEmployeeError(null));
          }}
          index={0}
        />
      )}

      {inviteSent && (
        <UPSnackBar
          vertical="bottom"
          horizontal="right"
          open={inviteSent}
          content={t(EMPLOYEES__INVITATION_SENT_SUCCESSFULLY)}
          type="success"
          onClose={() => {
            setInviteSent(false);
          }}
          index={0}
        />
      )}

      {promoteSuccessMessage && (
        <UPSnackBar
          vertical="bottom"
          horizontal="right"
          open
          content={t(promoteSuccessMessage)}
          type="success"
          onClose={() => {
            dispatch(setPromoteSuccessMessageAction(null));
          }}
          index={0}
        />
      )}

      {copiedToClipboard && (
        <UPSnackBar
          vertical="bottom"
          horizontal="right"
          open={copiedToClipboard}
          content={t(GENERAL__LINK_COPIED)}
          type="success"
          onClose={() => {
            setCopiedToClipboard(false);
          }}
          index={0}
        />
      )}
    </Box>
  );
};

export default Employees;
